import "./person-tease.scss";

import { MouseEventHandler, ReactNode } from "react";

import { Link } from "design-system/components/primitives/link/link";
import {
  Alignment,
  EditAttributes,
  TeaseStyle,
} from "design-system/types/types";
import { MediaAssetImageProps } from "design-system/components/primitives/media-asset/media-asset-image";
import {
  SocialMediaLinksProps,
  SocialMediaLinks,
} from "design-system/components/primitives/social-media-links/social-media-links";
import { MediaAsset } from "../media-asset/media-asset";

export interface PersonTeaseDisplayOptions {
  hideBio?: boolean;
  hidePhone?: boolean;
  hideJobTitle?: boolean;
  hideSocial?: boolean;
  emailVariant?: "email" | "icon" | "none";
  align?: Exclude<Alignment, "center">;
}

export interface PersonTeaseProps extends PersonTeaseDisplayOptions {
  bio?: ReactNode;
  shortBio?: string;
  isTeaseRow?: boolean;
  link?: string;
  name?: ReactNode;
  portrait?: MediaAssetImageProps;
  social?: SocialMediaLinksProps;
  style?: TeaseStyle;
  titles?: Array<string>;
  organization?: string;
  email?: string;
  phone?: string;
  type: "Person";
  HeadingLevel?: "h2" | "h3" | "h4";
  TeaseItem?: "div" | "li";
  onClick?: MouseEventHandler;
  editAttributes?: {
    name: EditAttributes;
    titles: EditAttributes;
  };
}

/**
 * ## See it in use on...
 * - The [people listing page](/example-pages-secondary-page-types-people-listing--story)
 *
 * - **`id: P-020-000-00`**
 */
export function PersonTease({
  bio,
  hideBio,
  shortBio,
  hidePhone,
  hideJobTitle,
  hideSocial,
  isTeaseRow,
  emailVariant = "icon",
  link,
  name,
  portrait,
  social,
  titles,
  organization,
  email,
  phone,
  HeadingLevel = "h3",
  TeaseItem = "div",
  editAttributes,
  onClick,
}: PersonTeaseProps) {
  const generateTitles = () => {
    if (hideJobTitle || !titles) return null;
    if (titles?.length && Array.isArray(titles)) {
      return (
        <p className="hbs-person-tease__title" {...editAttributes?.titles}>
          <span>{titles.join(", ")}</span>
        </p>
      );
    }
  };

  // if no portrait or src is empty string, use default avatar image.
  const imageAvatar = {
    src: "https://www.hbs.edu/static-assets/images/hbs/no-photo-square-400x400.jpg",
    alt: "",
  };
  const portraitAvatar =
    !portrait || portrait.src === "" ? imageAvatar : portrait;

  return (
    <TeaseItem className="hbs-person-tease">
      {/* Portrait, if no Portrait provided use Avatar */}
      {portraitAvatar && (
        <div className="hbs-person-tease__portrait">
          <MediaAsset
            aspectRatio="1/1"
            image={{ ...portraitAvatar, width: "400px", height: "400px" }}
          />
        </div>
      )}

      {/* Head */}
      <div className="hbs-person-tease__head">
        <HeadingLevel
          className="hbs-person-tease__name"
          {...editAttributes?.name}
        >
          {link ? (
            <Link href={link} onClick={onClick}>
              {name}
            </Link>
          ) : (
            <>{name}</>
          )}
        </HeadingLevel>

        {generateTitles()}

        {shortBio && !hideBio && (
          <div className="hbs-person-tease__short-bio">{shortBio}</div>
        )}

        {/* Short term fix for treatment of department in Person Listing: CMS-2185  */}
        {organization && !hideJobTitle && (
          <p className="hbs-person-tease__organization">{organization}</p>
        )}

        {((email && emailVariant === "email") || (phone && !hidePhone)) && (
          <div className="hbs-person-tease__contact">
            {email && emailVariant === "email" && (
              <div className="hbs-person-tease__contact-link">
                <Link href={`mailto:${email}`} onClick={onClick}>
                  {email}
                </Link>
              </div>
            )}

            {phone && !hidePhone && (
              <div className="hbs-person-tease__contact-link">
                <Link href={`tel:${phone}`} onClick={onClick}>
                  {phone}
                </Link>
              </div>
            )}
          </div>
        )}

        {social && !isTeaseRow && (!hideSocial || emailVariant === "icon") && (
          <div className="hbs-person-tease__social">
            <SocialMediaLinks {...social} />
          </div>
        )}
      </div>

      {/* Bio */}
      {!isTeaseRow && !hideBio && (
        <div className="hbs-person-tease__bio">{bio}</div>
      )}
    </TeaseItem>
  );
}
